import React from 'react';
import { isSectionVisible } from '../../utils/privacyUtils';
import { useAuth } from '../../hooks/useAuth';

/**
 * PrivacySectionWrapper - A component that conditionally renders content based on privacy settings
 * @param {Object} props
 * @param {string} props.section - The section type ('contact', 'experience', 'skills', 'portfolio')
 * @param {Object} props.profileData - The profile data containing privacy settings
 * @param {React.ReactNode} props.children - The content to render if visible
 * @param {React.ReactNode} props.fallback - Optional fallback content when section is hidden
 * @param {string} props.fallbackMessage - Default message when section is hidden
 * @param {boolean} props.showFallback - Whether to show fallback content or nothing when hidden
 * @returns {React.ReactNode}
 */
const PrivacySectionWrapper = ({
  section,
  profileData,
  children,
  fallback = null,
  fallbackMessage = 'This information is private',
  showFallback = false
}) => {
  const { user } = useAuth();

  // Check if this is the user's own profile
  const isOwnProfile = user?.id === profileData?.id;

  // Get privacy settings from profile data
  const privacySettings = profileData?.privacy_settings || {};

  // Check if the section should be visible
  const isVisible = isSectionVisible(section, privacySettings, isOwnProfile);

  // If section is not visible
  if (!isVisible) {
    if (showFallback) {
      return fallback || (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
          <div className="text-gray-500 mb-2">
            <svg 
              className="w-8 h-8 mx-auto mb-2 text-gray-400" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={1.5} 
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
              />
            </svg>
          </div>
          <p className="text-sm text-gray-600 font-medium">{fallbackMessage}</p>
          <p className="text-xs text-gray-500 mt-1">
            This user has chosen to keep this information private
          </p>
        </div>
      );
    }
    return null;
  }

  // Section is visible, render children
  return <>{children}</>;
};

/**
 * PrivacyFieldWrapper - A component that conditionally renders individual fields based on privacy settings
 * @param {Object} props
 * @param {string} props.section - The section type that this field belongs to
 * @param {Object} props.profileData - The profile data containing privacy settings
 * @param {React.ReactNode} props.children - The field content to render if visible
 * @param {string} props.fallback - Fallback text when field is hidden (e.g., "Hidden", "Private")
 * @param {boolean} props.showFallback - Whether to show fallback or hide completely
 * @returns {React.ReactNode}
 */
export const PrivacyFieldWrapper = ({
  section,
  profileData,
  children,
  fallback = 'Hidden',
  showFallback = false
}) => {
  const { user } = useAuth();

  // Check if this is the user's own profile
  const isOwnProfile = user?.id === profileData?.id;

  // Get privacy settings from profile data
  const privacySettings = profileData?.privacy_settings || {};

  // Check if the section should be visible
  const isVisible = isSectionVisible(section, privacySettings, isOwnProfile);

  if (!isVisible) {
    if (showFallback) {
      return <span className="text-gray-400 italic">{fallback}</span>;
    }
    return null;
  }

  return <>{children}</>;
};

/**
 * usePrivacyCheck - A hook to check privacy visibility for a section
 * @param {string} section - The section type
 * @param {Object} profileData - The profile data containing privacy settings
 * @returns {Object} Object containing visibility status and helper functions
 */
export const usePrivacyCheck = (section, profileData) => {
  const { user } = useAuth();

  const isOwnProfile = user?.id === profileData?.id;
  const privacySettings = profileData?.privacy_settings || {};
  const isVisible = isSectionVisible(section, privacySettings, isOwnProfile);

  return {
    isVisible,
    isOwnProfile,
    privacySettings,
    canEdit: isOwnProfile
  };
};

/**
 * Higher-order component that wraps a component with privacy checking
 * @param {React.Component} WrappedComponent - The component to wrap
 * @param {string} section - The section type for privacy checking
 * @param {Object} options - Options for the privacy wrapper
 * @returns {React.Component} - The privacy-wrapped component
 */
export const withPrivacyCheck = (WrappedComponent, section, options = {}) => {
  const {
    fallbackMessage = 'This section is private',
    showFallback = false
  } = options;

  return function PrivacyWrappedComponent(props) {
    const { profileData, ...restProps } = props;

    return (
      <PrivacySectionWrapper
        section={section}
        profileData={profileData}
        fallbackMessage={fallbackMessage}
        showFallback={showFallback}
      >
        <WrappedComponent {...restProps} profileData={profileData} />
      </PrivacySectionWrapper>
    );
  };
};

export default PrivacySectionWrapper;